<template>
  <div class="instructions-view">
    <DataTableComponent
      title="Instructions de l'IA"
      :data="prompts"
      :columns="columns"
      :loading="loading"
      :globalFilterFields="['title', 'content']"
      @update:selection="handleSelection"
    >
      <template #header-actions>
        <Button
          icon="pi pi-plus"
          label="Nouvelle instruction"
          class="p-button-success mr-2"
          @click="openNew"
        />
      </template>

      <template #isActive="{ row }">
        <Tag :value="row.isActive ? 'Actif' : 'Inactif'" :severity="row.isActive ? 'success' : 'danger'" />
      </template>

      <template #row-actions="{ row }">
        <div class="table-actions">
          <Button
            icon="pi pi-pencil"
            class="p-button-text p-button-rounded"
            @click="editPrompt(row)"
          />
          <Button
            icon="pi pi-trash"
            class="p-button-text p-button-rounded p-button-danger"
            @click="confirmDelete(row)"
          />
        </div>
      </template>
    </DataTableComponent>

    <Dialog
      v-model:visible="promptDialog"
      :header="editMode ? 'Modifier l\'instruction' : 'Nouvelle instruction'"
      :modal="true"
      class="p-fluid"
    >
      <div class="field">
        <label for="title">Titre</label>
        <InputText
          id="title"
          v-model="prompt.title"
          required
          autofocus
          :class="{ 'p-invalid': submitted && !prompt.title }"
        />
        <small class="p-error" v-if="submitted && !prompt.title">Le titre est requis.</small>
      </div>

      <div class="field">
        <label for="content">Contenu</label>
        <Textarea
          id="content"
          v-model="prompt.content"
          required
          rows="5"
          :class="{ 'p-invalid': submitted && !prompt.content }"
        />
        <small class="p-error" v-if="submitted && !prompt.content">Le contenu est requis.</small>
      </div>

      <div class="field">
        <label for="isActive">Statut</label>
        <InputSwitch v-model="prompt.isActive" />
      </div>

      <template #footer>
        <Button
          label="Annuler"
          icon="pi pi-times"
          class="p-button-text"
          @click="hideDialog"
        />
        <Button
          label="Enregistrer"
          icon="pi pi-check"
          class="p-button-text"
          @click="savePrompt"
        />
      </template>
    </Dialog>

    <Dialog
      v-model:visible="deletePromptDialog"
      :modal="true"
      header="Confirmation"
    >
      <div class="confirmation-content">
        <i class="pi pi-exclamation-triangle mr-3" style="font-size: 2rem" />
        <span>Êtes-vous sûr de vouloir supprimer cette instruction ?</span>
      </div>
      <template #footer>
        <Button
          label="Non"
          icon="pi pi-times"
          class="p-button-text"
          @click="deletePromptDialog = false"
        />
        <Button
          label="Oui"
          icon="pi pi-check"
          class="p-button-text"
          @click="deletePrompt"
        />
      </template>
    </Dialog>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue'
import { useToast } from 'primevue/usetoast'
import DataTableComponent from '@/components/common/DataTable.vue'
import Button from 'primevue/button'
import Dialog from 'primevue/dialog'
import InputText from 'primevue/inputtext'
import Textarea from 'primevue/textarea'
import InputSwitch from 'primevue/inputswitch'
import Tag from 'primevue/tag'
import axios from 'axios'

export default {
  name: 'InstructionsView',
  components: {
    DataTableComponent,
    Button,
    Dialog,
    InputText,
    Textarea,
    InputSwitch,
    Tag
  },
  setup() {
    const toast = useToast()
    const prompts = ref([])
    const promptDialog = ref(false)
    const deletePromptDialog = ref(false)
    const prompt = ref({})
    const selectedPrompt = ref(null)
    const submitted = ref(false)
    const loading = ref(false)
    const editMode = ref(false)

    const columns = [
      { field: 'title', header: 'Titre', sortable: true },
      { field: 'isActive', header: 'Statut', sortable: true, template: true },
      { field: 'row-actions', header: 'Actions', template: true }
    ]

    const loadPrompts = async () => {
      loading.value = true
      try {
        const response = await axios.get('/prompts')
        prompts.value = response.data
      } catch (error) {
        toast.add({
          severity: 'error',
          summary: 'Erreur',
          detail: 'Impossible de charger les instructions',
          life: 3000
        })
      } finally {
        loading.value = false
      }
    }

    const openNew = () => {
      prompt.value = {
        title: '',
        content: '',
        isActive: true
      }
      submitted.value = false
      promptDialog.value = true
      editMode.value = false
    }

    const editPrompt = (data) => {
      prompt.value = { ...data }
      promptDialog.value = true
      editMode.value = true
    }

    const confirmDelete = (data) => {
      selectedPrompt.value = data
      deletePromptDialog.value = true
    }

    const hideDialog = () => {
      promptDialog.value = false
      submitted.value = false
    }

    const savePrompt = async () => {
      submitted.value = true

      if (!prompt.value.title || !prompt.value.content) {
        return
      }

      try {
        if (editMode.value) {
          await axios.put(`/prompts/${prompt.value._id}`, prompt.value)
          toast.add({
            severity: 'success',
            summary: 'Succès',
            detail: 'Instruction mise à jour',
            life: 3000
          })
        } else {
          await axios.post('/prompts', prompt.value)
          toast.add({
            severity: 'success',
            summary: 'Succès',
            detail: 'Instruction créée',
            life: 3000
          })
        }

        promptDialog.value = false
        loadPrompts()
      } catch (error) {
        toast.add({
          severity: 'error',
          summary: 'Erreur',
          detail: 'Une erreur est survenue',
          life: 3000
        })
      }
    }

    const deletePrompt = async () => {
      try {
        await axios.delete(`/prompts/${selectedPrompt.value._id}`)
        deletePromptDialog.value = false
        loadPrompts()
        toast.add({
          severity: 'success',
          summary: 'Succès',
          detail: 'Instruction supprimée',
          life: 3000
        })
      } catch (error) {
        toast.add({
          severity: 'error',
          summary: 'Erreur',
          detail: 'Une erreur est survenue',
          life: 3000
        })
      }
    }

    onMounted(() => {
      loadPrompts()
    })

    return {
      prompts,
      columns,
      loading,
      promptDialog,
      deletePromptDialog,
      prompt,
      submitted,
      editMode,
      openNew,
      hideDialog,
      savePrompt,
      editPrompt,
      deletePrompt,
      confirmDelete
    }
  }
}
</script>

<style lang="scss" scoped>
.instructions-view {
  padding: 1rem;
}

.table-actions {
  display: flex;
  gap: 0.5rem;
  justify-content: center;
}

.confirmation-content {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
}
</style> 